import { useEffect, useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import React from 'react';

// Create a context to track modal state
interface ModalContextType {
  isAnyModalOpen: boolean;
}

const ModalContext = createContext<ModalContextType>({
  isAnyModalOpen: false
});

// Create a provider component to track modal state
export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);
  
  // Create a ref to store the portal element
  // This prevents re-creating the element on every render
  const [portalElement] = useState(() => {
    // Check if we're in the browser
    if (typeof document === 'undefined') return null;
    
    // Look for an existing portal container
    let element = document.getElementById('modal-portal-root');
    
    // If it doesn't exist, create it
    if (!element) {
      element = document.createElement('div');
      element.id = 'modal-portal-root';
      element.style.position = 'fixed';
      element.style.zIndex = '100000'; // Super high z-index
      element.style.top = '0';
      element.style.left = '0';
      element.style.width = '100%';
      element.style.height = '100%';
      element.style.pointerEvents = 'none'; // Don't block clicks on the page
      document.body.appendChild(element);
    }
    
    return element;
  });
  
  // Monitor the portal element for changes to track modal state
  useEffect(() => {
    if (!portalElement) return;
    
    const observer = new MutationObserver(() => {
      // Check if there are any visible children in the modal portal
      const hasVisibleChildren = portalElement.children.length > 0;
      setIsAnyModalOpen(hasVisibleChildren);
      
      // Set a data attribute on the body for easier CSS targeting
      if (hasVisibleChildren) {
        document.body.setAttribute('data-modal-open', 'true');
      } else {
        document.body.removeAttribute('data-modal-open');
      }
    });
    
    observer.observe(portalElement, { 
      childList: true, 
      subtree: true 
    });
    
    return () => {
      observer.disconnect();
    };
  }, [portalElement]);
  
  return (
    <ModalContext.Provider value={{ isAnyModalOpen }}>
      {children}
    </ModalContext.Provider>
  );
};

// Hook to access modal state
export const useModalContext = () => useContext(ModalContext);

interface ModalPortalProps {
  children: React.ReactNode;
}

// Memoize the ModalPortal component to prevent unnecessary re-renders
export const ModalPortal: React.FC<ModalPortalProps> = React.memo(({ children }) => {
  // Use the state for the portal element from above
  const [portalElement] = useState(() => {
    // Check if we're in the browser
    if (typeof document === 'undefined') return null;
    
    return document.getElementById('modal-portal-root');
  });

  // Only render when we have a portal element
  if (!portalElement) return null;
  
  return createPortal(
    // We wrap children in a div with pointer-events: auto to make them interactive
    <div style={{ pointerEvents: 'auto' }}>
      {children}
    </div>,
    portalElement
  );
});

ModalPortal.displayName = 'ModalPortal';

export default ModalPortal;